import React, { useState } from 'react';
import { SalesTrendResult } from '@/types/dashboard';

interface SalesTrendChartProps {
  trend: SalesTrendResult | null;
  hasFinancialAccess: boolean;
}

export default function SalesTrendChart({
  trend,
  hasFinancialAccess,
}: SalesTrendChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'orders' | 'revenue'>('all');

  if (!trend || !trend.data || trend.data.length === 0) {
    return (
      <div className="rounded-xl bg-white p-6 shadow-xs border border-gray-200/80">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <span>📈</span>
          <span>Sales &amp; Order Volume Trend</span>
        </h3>
        <div className="flex h-56 flex-col items-center justify-center text-gray-400">
          <svg
            className="h-10 w-10 text-gray-300 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className="text-sm font-semibold text-gray-600">No multi-channel orders in this timeframe</p>
          <p className="text-xs text-gray-400 mt-0.5">Switch presets or date filters above to expand range</p>
        </div>
      </div>
    );
  }

  const data = trend.data;
  const showFinancial = Boolean(hasFinancialAccess && data.some((d) => d.grossSales !== undefined && d.grossSales !== null));

  // Totals & Averages calculation
  const totalPeriodOrders = data.reduce((sum, d) => sum + (d.orders || 0), 0);
  const totalPeriodSales = showFinancial
    ? data.reduce((sum, d) => sum + (d.grossSales ? parseFloat(d.grossSales) : 0), 0)
    : 0;

  // Compute SVG dimensions and scales
  const maxOrders = Math.max(...data.map((d) => d.orders || 0), 4);
  const maxSales = showFinancial
    ? Math.max(
        ...data.map((d) => (d.grossSales ? parseFloat(d.grossSales) : 0)),
        1000,
      )
    : 0;

  const width = 800;
  const height = 240;
  const paddingX = 45;
  const paddingTop = 30;
  const paddingBottom = 40;
  const chartWidth = width - paddingX * 2;
  const chartHeight = height - paddingTop - paddingBottom;

  const stepX = data.length > 1 ? chartWidth / (data.length - 1) : chartWidth / 2;

  // Order points & SVG path
  const orderPoints = data.map((d, i) => {
    const x = data.length > 1 ? paddingX + i * stepX : width / 2;
    const y = paddingTop + chartHeight - (d.orders / maxOrders) * chartHeight;
    return { x, y, orders: d.orders, date: d.date, raw: d };
  });

  const orderLinePath =
    orderPoints.length > 1
      ? orderPoints.reduce(
          (acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`),
          '',
        )
      : '';

  const orderAreaPath =
    orderPoints.length > 1
      ? `${orderLinePath} L ${orderPoints[orderPoints.length - 1].x} ${paddingTop + chartHeight} L ${orderPoints[0].x} ${paddingTop + chartHeight} Z`
      : '';

  // Sales points & SVG path
  let salesPoints: { x: number; y: number; sales: number }[] = [];
  let salesLinePath = '';
  let salesAreaPath = '';

  if (showFinancial && maxSales > 0) {
    salesPoints = data.map((d, i) => {
      const x = data.length > 1 ? paddingX + i * stepX : width / 2;
      const val = d.grossSales ? parseFloat(d.grossSales) : 0;
      const y = paddingTop + chartHeight - (val / maxSales) * chartHeight;
      return { x, y, sales: val };
    });

    salesLinePath =
      salesPoints.length > 1
        ? salesPoints.reduce(
            (acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`),
            '',
          )
        : '';

    salesAreaPath =
      salesPoints.length > 1
        ? `${salesLinePath} L ${salesPoints[salesPoints.length - 1].x} ${paddingTop + chartHeight} L ${salesPoints[0].x} ${paddingTop + chartHeight} Z`
        : '';
  }

  const hoveredItem = hoveredIndex !== null ? data[hoveredIndex] : null;

  return (
    <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80">
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span>📈</span>
            <span>Sales &amp; Order Trajectory</span>
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {showFinancial
              ? 'Multi-channel order fulfillment and gross sales velocity'
              : 'Multi-channel daily order fulfillment volume'}
          </p>
        </div>

        {/* Legend / Filter Tabs */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100/70 p-0.5 rounded-lg text-xs font-semibold">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-2.5 py-1 rounded-md transition-all ${
                activeTab === 'all'
                  ? 'bg-white text-gray-900 shadow-xs'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('orders')}
              className={`px-2.5 py-1 rounded-md transition-all ${
                activeTab === 'orders'
                  ? 'bg-white text-indigo-700 shadow-xs'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Orders
            </button>
            {showFinancial && (
              <button
                type="button"
                onClick={() => setActiveTab('revenue')}
                className={`px-2.5 py-1 rounded-md transition-all ${
                  activeTab === 'revenue'
                    ? 'bg-white text-emerald-700 shadow-xs'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Revenue
              </button>
            )}
          </div>

          <div className="hidden sm:flex items-center gap-3 text-xs pl-2 border-l border-gray-200">
            {(activeTab === 'all' || activeTab === 'orders') && (
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-indigo-600 inline-block" />
                <span className="text-gray-600 font-semibold text-[11px]">Orders</span>
              </div>
            )}
            {showFinancial && (activeTab === 'all' || activeTab === 'revenue') && (
              <div className="flex items-center gap-1.5" data-testid="trend-financial-legend">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" />
                <span className="text-gray-600 font-semibold text-[11px]">Gross Sales (₹)</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hover Info Tooltip Banner */}
      <div className="h-7 mb-2 flex items-center">
        {hoveredItem ? (
          <div className="inline-flex items-center gap-3 bg-indigo-50/80 border border-indigo-100 px-3 py-1 rounded-lg text-xs animate-fadeIn">
            <span className="font-bold text-gray-900">{hoveredItem.date}</span>
            <span className="text-indigo-700 font-bold">
              📦 {hoveredItem.orders} orders
            </span>
            {showFinancial && hoveredItem.grossSales && (
              <span className="text-emerald-700 font-bold" data-testid="hover-financial-amount">
                💰 ₹{hoveredItem.grossSales}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-medium">Hover over points to inspect metrics</span>
        )}
      </div>

      {/* Native SVG Visualization */}
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto min-w-[500px]"
          data-testid="native-svg-chart"
        >
          <defs>
            <linearGradient id="orderGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.0" />
            </linearGradient>
            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, idx) => {
            const y = paddingTop + chartHeight * (1 - pct);
            return (
              <g key={idx}>
                <line
                  x1={paddingX}
                  y1={y}
                  x2={width - paddingX}
                  y2={y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                />
                <text
                  x={paddingX - 8}
                  y={y + 3}
                  textAnchor="end"
                  className="fill-gray-400 text-[10px] font-mono"
                >
                  {Math.round(maxOrders * pct)}
                </text>
              </g>
            );
          })}

          {/* Financial area & line path */}
          {showFinancial && (activeTab === 'all' || activeTab === 'revenue') && salesAreaPath && (
            <path d={salesAreaPath} fill="url(#salesGradient)" />
          )}
          {showFinancial && (activeTab === 'all' || activeTab === 'revenue') && salesLinePath && (
            <path
              d={salesLinePath}
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
          )}

          {/* Orders area & line path */}
          {(activeTab === 'all' || activeTab === 'orders') && orderAreaPath && (
            <path d={orderAreaPath} fill="url(#orderGradient)" />
          )}
          {(activeTab === 'all' || activeTab === 'orders') && orderLinePath && (
            <path
              d={orderLinePath}
              fill="none"
              stroke="#4f46e5"
              strokeWidth="2.5"
            />
          )}

          {/* Order Data Points & Hover Targets */}
          {orderPoints.map((p, idx) => (
            <g
              key={idx}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="cursor-pointer"
            >
              {/* Vertical Guide Line on Hover */}
              {hoveredIndex === idx && (
                <line
                  x1={p.x}
                  y1={paddingTop}
                  x2={p.x}
                  y2={paddingTop + chartHeight}
                  stroke="#6366f1"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              )}

              {/* Order Point Circle */}
              <circle
                cx={p.x}
                cy={p.y}
                r={hoveredIndex === idx ? 7 : 4.5}
                className={`${hoveredIndex === idx ? 'fill-indigo-600 stroke-indigo-200 stroke-4' : 'fill-indigo-600 stroke-white stroke-2'} transition-all`}
              />

              <text
                x={p.x}
                y={height - 15}
                textAnchor="middle"
                className={`text-[10px] font-medium transition-all ${hoveredIndex === idx ? 'fill-indigo-700 font-bold' : 'fill-gray-400'}`}
              >
                {p.date.slice(5)}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* Snapshot Summary footer */}
      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-gray-50/70 rounded-lg p-2.5 border border-gray-100">
          <p className="text-[10px] uppercase font-bold text-gray-400">Period Total Orders</p>
          <p className="text-base font-extrabold text-gray-900 mt-0.5">{totalPeriodOrders} Units</p>
        </div>
        {showFinancial && (
          <div className="bg-emerald-50/50 rounded-lg p-2.5 border border-emerald-100">
            <p className="text-[10px] uppercase font-bold text-emerald-700">Gross Sales Value</p>
            <p className="text-base font-extrabold text-emerald-800 mt-0.5 font-mono">
              ₹{totalPeriodSales.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
          </div>
        )}
        <div className="bg-gray-50/70 rounded-lg p-2.5 border border-gray-100 col-span-2 sm:col-span-1">
          <p className="text-[10px] uppercase font-bold text-gray-400">Data Granularity</p>
          <p className="text-xs font-bold text-gray-700 mt-1">Daily Automated Rollup</p>
        </div>
      </div>
    </div>
  );
}

